import { useState } from 'react'
import { Link } from 'react-router'
import { format } from 'date-fns'
import { Search, ChevronLeft, ChevronRight, Gift } from 'lucide-react'
import { useGiftLicenses } from '@/hooks/useAdminUsers'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

export function GiftLicensesPage() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 50

  const { data, isLoading } = useGiftLicenses({
    page,
    pageSize,
    search: search || undefined,
  })

  const licenses = data?.licenses ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div>
      <PageHeader title="Gift Licenses" description={`${total} gift licenses`} />

      <div className="mb-6 flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by code or user ID…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : licenses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Gift className="mb-4 h-12 w-12" />
          <p>No gift licenses found.</p>
        </div>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                    <th className="px-4 py-2 text-left">Code</th>
                    <th className="px-4 py-2 text-left">Tier</th>
                    <th className="px-4 py-2 text-left">Duration</th>
                    <th className="px-4 py-2 text-left">Buyer</th>
                    <th className="px-4 py-2 text-left">Redeemer</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Created</th>
                    <th className="px-4 py-2 text-left">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((l) => (
                    <tr key={l.id} className="border-b last:border-0">
                      <td className="px-4 py-2">
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {l.code.slice(0, 4)}…{l.code.slice(-4)}
                        </code>
                      </td>
                      <td className="px-4 py-2 capitalize">{l.tier.replace('_', ' ')}</td>
                      <td className="px-4 py-2">{l.duration_days}d</td>
                      <td className="px-4 py-2">
                        {l.buyer_user_id ? (
                          <Link to={`/users/${l.buyer_user_id}`} className="text-primary hover:underline">
                            {l.buyer_email ?? l.buyer_user_id.slice(0, 8)}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {l.redeemed_by ? (
                          <Link to={`/users/${l.redeemed_by}`} className="text-primary hover:underline">
                            {l.redeemer_email ?? l.redeemed_by.slice(0, 8)}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Badge
                          variant="outline"
                          className={
                            l.status === 'redeemed'
                              ? 'border-green-300 bg-green-50 text-green-800'
                              : l.status === 'expired'
                                ? 'border-yellow-300 bg-yellow-50 text-yellow-900'
                                : ''
                          }
                        >
                          {l.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {format(new Date(l.created_at), 'MMM d, yyyy')}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {format(new Date(l.expires_at), 'MMM d, yyyy')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
